import {
  PaymentMethodIdScheme,
  ProductIdScheme,
  ProductPageIdScheme,
} from "./ids";
import { LayoutItemType } from "./enums/LayoutItemType";
import z from "zod";
import { DiscountScheme } from "./discount";
import { DiscountScope } from "./enums/DiscountScope";
import { LayoutShowDateFormat } from "./enums/LayoutShowDateFormat";
import { LayoutShowTimeTextSize } from "./enums/LayoutShowTimeTextSize";

const LayoutItemPositionScheme = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number().positive(),
  height: z.number().positive(),
});

const LayoutItemBaseScheme = z.object({
  id: z.string(),
  type: z.enum(LayoutItemType),
  text: z.string().nullable(),
  color: z.string().nullable(),
  icon: z.string().nullable(),
  mobilePosition: LayoutItemPositionScheme.optional(),
  tabletPosition: LayoutItemPositionScheme.optional(),
});

const LayoutItemProductScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.PRODUCT),
  productId: ProductIdScheme,
});

const LayoutItemProductPageScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.PRODUCT_PAGE),
  productPageId: ProductPageIdScheme,
});

const LayoutItemDiscountScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.DISCOUNT),
  setDiscount: DiscountScheme.nullable().optional(),
  discountScope: z.enum(DiscountScope).optional(),
});

const LayoutItemGiftCardScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.GIFT_CARD),
});

const LayoutItemProductSearchScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.PRODUCT_SEARCH),
});

const LayoutItemBarcodeModalScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.BARCODE_MODAL),
});

const LayoutItemMenuLinkScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.MENU_LINK),
  menuPage: z.string(),
});

const LayoutItemWebsiteScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.WEBSITE),
  url: z.string(),
});

const LayoutItemScaleValueScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.SCALE_VALUE),
});

const LayoutItemShowClockScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.SHOW_CLOCK),
  textSize: z.enum(LayoutShowTimeTextSize).optional(),
  showDate: z.boolean().optional(),
  showClock: z.boolean().optional(),
  showSeconds: z.boolean().optional(),
  dateFormat: z.enum(LayoutShowDateFormat).optional(),
});

const LayoutItemShowDateScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.SHOW_DATE),
  textSize: z.enum(LayoutShowTimeTextSize).optional(),
  dateFormat: z.enum(LayoutShowDateFormat).optional(),
});

const LayoutItemGiftCardLookupScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.GIFT_CARD_LOOKUP),
});

const LayoutItemOpenCashboxScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.OPEN_CASHBOX),
});

const LayoutItemActiveTabScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.ACTIVE_TAB),
});

const LayoutItemSplitTabScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.SPLIT_TAB),
});

const LayoutItemPrintProformaScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.PRINT_PROFORMA),
});

const LayoutItemForwardToPrepStationScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.FORWARD_PREP_STATION),
  requireChoiceOfCourse: z.boolean().optional(),
});

const LayoutItemWaitForPrepStationScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.WAIT_PREP_STATION),
  requireChoiceOfCourse: z.boolean().optional(),
});

const LayoutItemCombinedPrepTicketScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.COMBINED_PREP_TICKET),
});

const LayoutItemToggleTakeawayScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.TOGGLE_TAKEAWAY),
});

const LayoutItemTextItemScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.TEXT_ITEM),
  prefix: z.string(),
  value: z.string(),
});

const LayoutItemSelectStoreScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.SELECT_STORE),
});

const LayoutItemSetReferenceScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.SET_REFERENCE),
});

const LayoutItemOrderPageScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.ORDER_PAGE),
});

const LayoutItemStaffSelectScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.STAFF_SELECT),
});

const LayoutItemShowAllScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.SHOW_ALL),
});

const LayoutItemPaymentMethodScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.PAYMENT_METHOD),
  paymentMethodId: PaymentMethodIdScheme,
});

const LayoutItemSelectCustomerScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.SELECT_CUSTOMER),
});

const LayoutItemServiceOrderScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.SERVICE_ORDER),
});

const LayoutItemServiceOrderDepositScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.SERVICE_ORDER_DEPOSIT),
});

const LayoutItemTemporarilyOutOfStockScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.TEMPORARILY_OUT_OF_STOCK),
});

const LayoutItemProductLookupScheme = LayoutItemBaseScheme.extend({
  type: z.literal(LayoutItemType.PRODUCT_LOOKUP),
});

export const LayoutItemScheme = z.discriminatedUnion("type", [
  LayoutItemProductScheme,
  LayoutItemProductPageScheme,
  LayoutItemDiscountScheme,
  LayoutItemGiftCardScheme,
  LayoutItemGiftCardLookupScheme,
  LayoutItemProductSearchScheme,
  LayoutItemBarcodeModalScheme,
  LayoutItemMenuLinkScheme,
  LayoutItemWebsiteScheme,
  LayoutItemScaleValueScheme,
  LayoutItemShowClockScheme,
  LayoutItemShowDateScheme,
  LayoutItemOpenCashboxScheme,
  LayoutItemActiveTabScheme,
  LayoutItemSplitTabScheme,
  LayoutItemPrintProformaScheme,
  LayoutItemForwardToPrepStationScheme,
  LayoutItemWaitForPrepStationScheme,
  LayoutItemCombinedPrepTicketScheme,
  LayoutItemToggleTakeawayScheme,
  LayoutItemTextItemScheme,
  LayoutItemSelectStoreScheme,
  LayoutItemSetReferenceScheme,
  LayoutItemOrderPageScheme,
  LayoutItemStaffSelectScheme,
  LayoutItemShowAllScheme,
  LayoutItemPaymentMethodScheme,
  LayoutItemSelectCustomerScheme,
  LayoutItemServiceOrderScheme,
  LayoutItemServiceOrderDepositScheme,
  LayoutItemTemporarilyOutOfStockScheme,
  LayoutItemProductLookupScheme,
]);

export type LayoutItem = z.infer<typeof LayoutItemScheme>;
export type LayoutItemProduct = z.infer<typeof LayoutItemProductScheme>;
export type LayoutItemProductPage = z.infer<typeof LayoutItemProductPageScheme>;
export type LayoutItemDiscount = z.infer<typeof LayoutItemDiscountScheme>;
export type LayoutItemGiftCard = z.infer<typeof LayoutItemGiftCardScheme>;
export type LayoutItemGiftCardLookup = z.infer<
  typeof LayoutItemGiftCardLookupScheme
>;
export type LayoutItemProductSearch = z.infer<
  typeof LayoutItemProductSearchScheme
>;
export type LayoutItemBarcodeModal = z.infer<
  typeof LayoutItemBarcodeModalScheme
>;
export type LayoutItemMenuLink = z.infer<typeof LayoutItemMenuLinkScheme>;
export type LayoutItemWebsite = z.infer<typeof LayoutItemWebsiteScheme>;
export type LayoutItemScaleValue = z.infer<typeof LayoutItemScaleValueScheme>;
export type LayoutItemShowClock = z.infer<typeof LayoutItemShowClockScheme>;
export type LayoutItemShowDate = z.infer<typeof LayoutItemShowDateScheme>;
export type LayoutItemOpenCashbox = z.infer<typeof LayoutItemOpenCashboxScheme>;
export type LayoutItemActiveTab = z.infer<typeof LayoutItemActiveTabScheme>;
export type LayoutItemSplitTab = z.infer<typeof LayoutItemSplitTabScheme>;
export type LayoutItemPrintProforma = z.infer<
  typeof LayoutItemPrintProformaScheme
>;
export type LayoutItemForwardToPrepStation = z.infer<
  typeof LayoutItemForwardToPrepStationScheme
>;
export type LayoutItemWaitForPrepStation = z.infer<
  typeof LayoutItemWaitForPrepStationScheme
>;
export type LayoutItemCombinedPrepTicket = z.infer<
  typeof LayoutItemCombinedPrepTicketScheme
>;
export type LayoutItemToggleTakeaway = z.infer<
  typeof LayoutItemToggleTakeawayScheme
>;
export type LayoutItemTextItem = z.infer<typeof LayoutItemTextItemScheme>;
export type LayoutItemSelectStore = z.infer<typeof LayoutItemSelectStoreScheme>;
export type LayoutItemSetReference = z.infer<
  typeof LayoutItemSetReferenceScheme
>;
export type LayoutItemOrderPage = z.infer<typeof LayoutItemOrderPageScheme>;
export type LayoutItemStaffSelect = z.infer<typeof LayoutItemStaffSelectScheme>;
export type LayoutItemShowAll = z.infer<typeof LayoutItemShowAllScheme>;
export type LayoutItemPaymentMethod = z.infer<
  typeof LayoutItemPaymentMethodScheme
>;
export type LayoutItemSelectCustomer = z.infer<
  typeof LayoutItemSelectCustomerScheme
>;
export type LayoutItemServiceOrder = z.infer<
  typeof LayoutItemServiceOrderScheme
>;
export type LayoutItemServiceOrderDeposit = z.infer<
  typeof LayoutItemServiceOrderDepositScheme
>;
export type LayoutItemTemporarilyOutOfStock = z.infer<
  typeof LayoutItemTemporarilyOutOfStockScheme
>;
export type LayoutItemProductLookup = z.infer<
  typeof LayoutItemProductLookupScheme
>;
